import { type FormEvent, useCallback, useEffect, useState } from "react";
import { createToken, listTokens, revokeToken } from "../../api/client";
import type { ApiToken, NewToken } from "../../api/models";
import { errorMessage, formatDate } from "../../lib/format";

export function TokensPage() {
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<NewToken | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    listTokens()
      .then(setTokens)
      .catch((e) => setError(errorMessage(e)));
  }, []);
  useEffect(load, [load]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      setCreated(await createToken(name.trim()));
      setCopied(false);
      setName("");
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token: ApiToken) => {
    if (!window.confirm(`Revoke "${token.name}"? Anything still using it stops working.`)) return;
    try {
      await revokeToken(token.id);
      if (created?.token.id === token.id) setCreated(null);
      load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.secret);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const origin = window.location.origin;

  return (
    <>
      <div className="toolbar">
        <h1>API tokens</h1>
        <span className="grow" />
        <form className="toolbar" style={{ marginBottom: 0 }} onSubmit={create}>
          <input
            type="text"
            placeholder="Name, e.g. laptop or CI"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
          />
          <button type="submit" className="primary" disabled={creating || !name.trim()}>
            Create token
          </button>
        </form>
      </div>
      <p className="muted">
        A token pulls the dataset and submits reports from a terminal or CI as you. Reports sent with your token are
        listed only for you; the shared export token, used by CI, makes reports everyone can see. The routes a token can
        call are documented at{" "}
        <a href="/api/docs" target="_blank" rel="noreferrer">
          /api/docs
        </a>
        .
      </p>
      {error && <p className="error">{error}</p>}

      {created && (
        <div className="panel">
          <p>
            <strong>{created.token.name}</strong> — copy the token now; it is not shown again.
          </p>
          <p>
            <code className="mono">{created.secret}</code>{" "}
            <button type="button" onClick={copy}>
              {copied ? "Copied" : "Copy"}
            </button>
          </p>
          <p className="muted">
            In the hobby checkout, put it in <code className="mono">secrets/hobby.json</code> as{" "}
            <code className="mono">golden_token</code> (in place of the shared one), then:
          </p>
          <pre className="mono">
            {`just dataset-pull ${origin}   # the verified set into scan-core/test/dataset/cards\njust dataset                 # run the engine over it\njust dataset-submit "my change" ${origin}   # a private run here`}
          </pre>
        </div>
      )}

      {!tokens && !error && <p className="muted">Loading…</p>}
      {tokens && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Token</th>
                <th>Created</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="mono">{t.prefix}…</td>
                  <td className="nowrap">{formatDate(t.createdAt)}</td>
                  <td className="nowrap">{formatDate(t.lastUsedAt)}</td>
                  <td>
                    <button type="button" className="danger" onClick={() => revoke(t)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
              {tokens.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    No tokens yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
